import { IFirestoreListenerData, IFirestoreQueryData, IFirestoreRealtimeData } from '../core/types';

import { BaseFirestorePlugin } from '../core/BaseFirestorePlugin';

const LOG_STYLES = {
  query: '📥',
  warning: '⚠️',
  error: '❌',
  listener: '👂',
  listenerUpdate: '📝'
};

interface ConsoleLoggerConfig {
  logQueries?: boolean;
  logSlowQueriesOnly?: boolean;  // When true, only log queries above slowQueryThresholdMs
  slowQueryThresholdMs?: number; // Threshold for what constitutes a "slow" query
  logListenerEvents?: boolean;
  logRealtimeUpdates?: boolean;
}

export class ConsoleLoggerPlugin extends BaseFirestorePlugin {
  private config: ConsoleLoggerConfig;
  private readonly defaultConfig: ConsoleLoggerConfig = {
    logQueries: false,
    logSlowQueriesOnly: false,
    slowQueryThresholdMs: 1500, // Default 1.5 second threshold
    logListenerEvents: false,
    logRealtimeUpdates: false
  };

  constructor(config: ConsoleLoggerConfig = {}) {
    super('console-logger', 'Console Logger');
    this.enabled = true;
    this.config = { ...this.defaultConfig, ...config };
    console.log('[DEBUG] ConsoleLoggerPlugin initialized');
  }

  beforeQuery(data: IFirestoreQueryData) {
    // Still commented out as per original
    // if (!this.config.logQueries) return;
    // const [message, logData] = this.formatLogMessage('query', 'Query Started', data);
    // console.groupCollapsed(message);
    // console.table(logData);
    // console.groupEnd();
  }

  afterQuery(data: IFirestoreQueryData) {
    if (!this.config.logQueries) return;

    const duration = data.duration || 0;
    const threshold = this.config.slowQueryThresholdMs ?? 1000;
    if (this.config.logSlowQueriesOnly && duration < threshold) return;

    const [message, logData] = this.formatLogMessage('query', 'Query Completed', data);
    if (message && logData) {
      console.groupCollapsed(message);
      console.table(logData);
      console.groupEnd();
    }
  }

  afterListenerAdded(data: IFirestoreListenerData) {
    if (!this.config.logListenerEvents) return;

    const [message, logData] = this.formatLogMessage('listener', 'Listener Added', data);
    console.groupCollapsed(message);
    console.table(logData);
    console.groupEnd();
  }

  afterListenerRemoved(data: IFirestoreListenerData) {
    if (!this.config.logListenerEvents) return;

    const [message, logData] = this.formatLogMessage('listener', 'Listener Removed', data);
    console.groupCollapsed(message);
    console.table(logData);
    console.groupEnd();
  }

  afterRealtimeDataReceived(data: IFirestoreRealtimeData) {
    if (!this.config.logRealtimeUpdates) return;

    const [message, logData] = this.formatLogMessage('listenerUpdate', 'Realtime Update', data);
    console.groupCollapsed(message);
    console.table(logData);
    console.groupEnd();
  }

  private formatLogMessage(type: string, message: string, data: any): [string | null, any | null] {
    const baseData = { ...data };

    // If queryDetails exists, merge it with base data
    if (baseData.queryDetails) {
      const flattenedData = {
        ...baseData,
        ...baseData.queryDetails
      };
      delete flattenedData.queryDetails; // Remove the nested queryDetails object

      return [`[Firestore] [${type}] ${message}`, flattenedData];
    }

    return [`[Firestore] [${type}] ${message}`, baseData];
  }
}
